import { HelpCircle } from "lucide-react";
import { getInsightFAQs, type InsightFAQ } from "@/data/insightFAQs";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { Helmet } from "react-helmet-async";

interface InsightFAQSectionProps {
  insightSlug: string;
  title?: string;
}

// Generate JSON-LD FAQ schema for SEO
const generateFAQSchema = (faqs: InsightFAQ[]) => {
  return {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: faqs.map((faq) => ({
      "@type": "Question",
      name: faq.question,
      acceptedAnswer: {
        "@type": "Answer",
        text: faq.answer,
      },
    })),
  };
};

export const InsightFAQSection = ({
  insightSlug,
  title = "Frequently Asked Questions",
}: InsightFAQSectionProps) => {
  const faqs = getInsightFAQs(insightSlug);

  if (faqs.length === 0) return null;

  const faqSchema = generateFAQSchema(faqs);

  return (
    <section className="mt-12 sm:mt-16">
      {/* FAQ Schema for SEO */}
      <Helmet>
        <script type="application/ld+json">{JSON.stringify(faqSchema)}</script>
      </Helmet>

      <div className="flex items-center gap-2 mb-6">
        <HelpCircle className="w-5 h-5 text-primary" />
        <h2 className="font-display text-xl font-semibold text-foreground">
          {title}
        </h2>
      </div>

      <div className="bg-muted/30 rounded-xl border border-border p-4 sm:p-6">
        <Accordion type="single" collapsible className="space-y-2">
          {faqs.map((faq, index) => (
            <AccordionItem
              key={index}
              value={`faq-${index}`}
              className="border border-border rounded-lg px-4 bg-background data-[state=open]:bg-primary/5 data-[state=open]:border-primary/30"
            >
              <AccordionTrigger className="text-sm sm:text-base font-medium text-foreground hover:text-primary hover:no-underline py-4 text-left">
                {faq.question}
              </AccordionTrigger>
              <AccordionContent className="text-sm text-muted-foreground pb-4 leading-relaxed">
                {faq.answer}
              </AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </div>
    </section>
  );
};
